import {
  AuditOutlined,
  BankOutlined,
  DashboardOutlined,
  FileTextOutlined,
  MenuOutlined,
  OrderedListOutlined,
  SettingOutlined,
  ShoppingOutlined,
  SyncOutlined,
  TeamOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Button, Drawer, Layout, Menu, theme as antTheme, type MenuProps } from 'antd';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useIsMobile } from '../lib/responsive';
import { FirmaSelector } from './FirmaSelector';
import { UserMenu } from './UserMenu';

const { Sider, Header, Content } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

const MENU_ITEMS: MenuItem[] = [
  { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: '/facturi', icon: <FileTextOutlined />, label: 'Facturi' },
  { key: '/cheltuieli', icon: <WalletOutlined />, label: 'Cheltuieli' },
  { key: '/contabilitate', icon: <AuditOutlined />, label: 'Contabilitate' },
  { key: '/abonamente', icon: <SyncOutlined />, label: 'Abonamente' },
  { key: '/clienti', icon: <TeamOutlined />, label: 'Clienți' },
  { key: '/produse', icon: <ShoppingOutlined />, label: 'Produse' },
  { type: 'divider' },
  { key: '/firme', icon: <BankOutlined />, label: 'Firme' },
  { key: '/serii', icon: <OrderedListOutlined />, label: 'Serii' },
  { key: '/setari', icon: <SettingOutlined />, label: 'Setări' },
];

/** Prefix-match: `/facturi/noua` still highlights the "Facturi" menu item.
 * Every top-level segment maps 1:1 to a menu key here in Task 8a. */
function activeKeyFor(pathname: string): string {
  if (pathname === '/') return '/';
  const [, segment] = pathname.split('/');
  return `/${segment}`;
}

/** The brand wordmark, shared by the desktop Sider and the mobile Drawer. */
function Brand({ collapsed }: { collapsed?: boolean }) {
  return (
    <div
      className="brand"
      style={{
        color: '#fff',
        fontSize: collapsed ? 20 : 24,
        textAlign: 'center',
        padding: '16px 0',
      }}
    >
      {collapsed ? 'F' : 'Financiar'}
    </div>
  );
}

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  // Mobile: the menu lives in an off-canvas Drawer toggled by the hamburger,
  // instead of the always-present Sider. Closed by default; closes again once a
  // rubric is picked so the drawer never sits on top of the page you navigated
  // to.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const {
    token: { colorBgContainer },
  } = antTheme.useToken();

  // Leaving mobile (rotate to landscape / resize) must not strand the drawer open.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const menu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[activeKeyFor(location.pathname)]}
      items={MENU_ITEMS}
      onClick={({ key }) => {
        navigate(key);
        setDrawerOpen(false);
      }}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={240}
          styles={{ body: { padding: 0, background: '#001529' }, header: { display: 'none' } }}
        >
          <Brand />
          {menu}
        </Drawer>
      ) : (
        <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed}>
          <Brand collapsed={collapsed} />
          {menu}
        </Sider>
      )}
      <Layout>
        <Header
          style={{
            background: colorBgContainer,
            padding: isMobile ? '0 12px' : '0 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            justifyContent: 'space-between',
          }}
        >
          {isMobile ? (
            <Button
              type="text"
              aria-label="Meniu"
              icon={<MenuOutlined />}
              onClick={() => setDrawerOpen(true)}
              style={{ flex: '0 0 auto' }}
            />
          ) : null}
          <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center' }}>
            <FirmaSelector />
          </div>
          <div style={{ flex: '0 0 auto' }}>
            <UserMenu />
          </div>
        </Header>
        <Content style={{ margin: isMobile ? 12 : 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
